import { filterActions, FilterTemplates, FilterParams, getAuto, AxisType, TypeSize, getParams } from '@entities/Filter';
import { sortSizeDisks } from '@app/helpers/sortSizeDisks';
import { sortSizeTyres } from '@app/helpers/sortSizeTyres';
import { RadioButton } from '@app/shared';
import { DefaultProps } from '@app/Types/global.interfaces';
import { RecSizeTyre, RecSizeDisk, RecSize } from '../../module/types/autoSelectsTypes';
import { RecommendedSizesProps } from './Types';
import { useEffect, useState } from 'react';
import { generateTyresTitleRadioButton } from '@entities/Auto';
import { groupElementsByConnections } from '@entities/Auto/module/helpers/groupSize';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { ProductSpecifications } from '@entities/Product';
import classNames from 'classnames';

import styles from './RecommendedSizes.module.scss';

const getTypeAxis = (Axis: AxisType): string => {
  switch (Axis) {
    case AxisType.frontAxles:
      return 'передняя ось';
    case AxisType.rearAxles:
      return 'задняя ось';
    default:
      return 'для всех осей';
  }
};

const getTitleTypeSize = (type: TypeSize): string => {
  switch (type) {
    case TypeSize.factory:
      return 'Заводская комплектация';
    case TypeSize.replacement:
      return 'Варианты замены';
    default:
      return 'Варианты тюнинга';
  }
};

const getBlockTypeTestId = (type: TypeSize): string => {
  switch (type) {
    case TypeSize.factory:
      return 'factory';
    case TypeSize.replacement:
      return 'replacement';
    default:
      return 'tuning';
  }
};

const compare = (val1, val2) => {
  if (val1 > val2) {
    return 1;
  }
  if (val1 < val2) {
    return -1;
  }
  return 0;
};

const sortAxis = (a: RecSize, b: RecSize) => {
  return compare(Number(a.axis), Number(b.axis));
};

function getSizesTypeSize<T>(
  sizes: Array<T & { type: TypeSize; slaveId: number; id: number }>,
): Array<{ items: T[]; type: TypeSize; sparky: T[][] }> {
  return [
    {
      type: TypeSize.factory,
      items: sizes.filter(size => size.type === TypeSize.factory && !size.slaveId),
      sparky: groupElementsByConnections(sizes.filter(size => size.type === TypeSize.factory && size.slaveId)),
    },
    {
      type: TypeSize.replacement,
      items: sizes.filter(size => size.type === TypeSize.replacement && !size.slaveId),
      sparky: groupElementsByConnections(sizes.filter(size => size.type === TypeSize.replacement && size.slaveId)),
    },
    {
      type: TypeSize.tuning,
      items: sizes.filter(size => size.type === TypeSize.tuning && !size.slaveId),
      sparky: groupElementsByConnections(sizes.filter(size => size.type === TypeSize.tuning && size.slaveId)),
    },
  ];
}

export function RecommendedSizes({ sizes, className, type }: RecommendedSizesProps & DefaultProps) {
  if (!sizes.tyresSizes && !sizes.disksSizes) {
    return null;
  }

  return (
    <div className={classNames(styles.Wrapper, className)}>
      {[FilterTemplates.Tyres, FilterTemplates.TyresModels].includes(type) ? (
        <RecommendedSizes.Tyres sizes={sizes.tyresSizes} />
      ) : (
        <RecommendedSizes.Disks sizes={sizes.disksSizes} />
      )}
    </div>
  );
}

RecommendedSizes.Tyres = function Tyres({ sizes }: { sizes: RecSizeTyre[] }) {
  const params = useAppSelector(getParams);
  const [currentSize, setCurrentSize] = useState<string>('');
  const { modification } = useAppSelector(getAuto);
  const array = getSizesTypeSize(sizes);

  useEffect(() => {
    setCurrentSize(
      `${params[ProductSpecifications.TyresWidth]}/${params[ProductSpecifications.TyresProfile]}` +
        `R${params[ProductSpecifications.TyresDiameter]}`,
    );
  }, [params]);

  return (
    <div className={styles.Container}>
      {array.map(block => {
        const isHidAxis = !block.items.some(x => [AxisType.frontAxles, AxisType.rearAxles].includes(x.axis));
        if (!block.items.length && !block.sparky.flat().length) {
          return null;
        }

        return (
          <div key={block.type} className={styles.Block} data-testid={`block-${getBlockTypeTestId(block.type)}`}>
            <div className={styles.Title}>{getTitleTypeSize(block.type)}</div>
            <div className={styles.Items}>
              {block.items.sort(sortSizeTyres).map(size => {
                const title = generateTyresTitleRadioButton(size.width, size.height, size.diameter);
                const type = getTypeAxis(size.axis);
                const tyreSize = [
                  { key: ProductSpecifications.TyresWidth, value: size.width + '' },
                  { key: ProductSpecifications.TyresProfile, value: size.height + '' },
                  { key: ProductSpecifications.TyresDiameter, value: size.diameter + '' },
                  { key: FilterParams.Axis, value: size.axis + '' },
                  { key: FilterParams.ModificationAuto, value: modification?.id + '' },
                ];

                const handlerSetCurrentSizeType = (value: string) => {
                  setCurrentSize(value);
                };

                return (
                  <RecommendedSizes.Checkbox
                    key={`${size.width}/${size.height}` + `R${size.diameter} ${type} ${block.type}`}
                    groupRadioButton={block.type}
                    title={title}
                    type={type}
                    size={tyreSize}
                    value={`${size.width}/${size.height}` + `R${size.diameter}`}
                    currentValue={currentSize}
                    isHidAxis={isHidAxis}
                    setCurrentSize={handlerSetCurrentSizeType}
                  />
                );
              })}
              <div className={styles.WrapperContainerSparky}>
                {block.sparky.map((groupSparky, idx) => {
                  if (groupSparky.length === 0) {
                    return null;
                  }

                  const isHidAxisSparky = !groupSparky.some(x => [AxisType.frontAxles, AxisType.rearAxles].includes(x.axis));

                  return (
                    <div className={styles.WrapperGroupSparky} key={idx}>
                      <div className={styles.Line} />
                      <div className={styles.SparkyItems}>
                        {groupSparky
                          .sort(sortSizeTyres)
                          .sort(sortAxis)
                          .map(size => {
                            const title = generateTyresTitleRadioButton(size.width, size.height, size.diameter);
                            const type = getTypeAxis(size.axis);
                            const tyreSize = [
                              { key: ProductSpecifications.TyresWidth, value: size.width + '' },
                              { key: ProductSpecifications.TyresProfile, value: size.height + '' },
                              { key: ProductSpecifications.TyresDiameter, value: size.diameter + '' },
                              { key: FilterParams.Axis, value: size.axis + '' },
                              { key: FilterParams.ModificationAuto, value: modification?.id + '' },
                            ];

                            const handlerSetCurrentSizeType = (value: string) => {
                              setCurrentSize(value);
                            };

                            return (
                              <RecommendedSizes.Checkbox
                                key={`${size.width}/${size.height}` + `R${size.diameter} ${type} ${block.type}`}
                                groupRadioButton={block.type}
                                title={title}
                                type={type}
                                size={tyreSize}
                                value={`${size.width}/${size.height}` + `R${size.diameter}`}
                                className={styles.SparkyCheckbox}
                                currentValue={currentSize}
                                isHidAxis={isHidAxisSparky}
                                setCurrentSize={handlerSetCurrentSizeType}
                              />
                            );
                          })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

RecommendedSizes.Disks = function Disks({ sizes }: { sizes: RecSizeDisk[] }) {
  const [currentSize, setCurrentSize] = useState<string>('');
  const { modification } = useAppSelector(getAuto);
  const array = getSizesTypeSize(sizes);
  const params = useAppSelector(getParams);

  useEffect(() => {
    setCurrentSize(
      `${params[ProductSpecifications.DiscsWidth]}x${params[ProductSpecifications.DiscsDiameter]}` +
        `${params[ProductSpecifications.DiscsPcd]} ET${params[ProductSpecifications.DiscsEt]}` +
        `Dia${params[ProductSpecifications.DiscsDia]}`,
    );
  }, [params]);

  return (
    <div className={styles.Container}>
      {array.map(block => {
        const isHidAxis = !block.items.some(x => [AxisType.frontAxles, AxisType.rearAxles].includes(x.axis));

        if (!block.items.length) {
          return null;
        }

        return (
          <div key={block.type} className={styles.Block} data-testid={`block-${getBlockTypeTestId(block.type)}`}>
            <div className={styles.Title}>{getTitleTypeSize(block.type)}</div>
            <div className={styles.Items}>
              {block.items.sort(sortSizeDisks).map(size => {
                const title = `${size.width}x${size.diameter} ET${size.et}`;
                const type = getTypeAxis(size.axis);
                const diskSize = [
                  { key: ProductSpecifications.DiscsDiameter, value: size.diameter + '' },
                  { key: ProductSpecifications.DiscsPcd, value: `${size.PCDc}*${size.PCDd}` },
                  { key: ProductSpecifications.DiscsDia, value: size.dia + '' },
                  { key: ProductSpecifications.MinDiscsDia, value: '' },
                  { key: ProductSpecifications.MaxDiscsDia, value: '' },
                  { key: ProductSpecifications.DiscsEt, value: size.et + '' },
                  { key: ProductSpecifications.MinDiscsEt, value: '' },
                  { key: ProductSpecifications.MaxDiscsEt, value: '' },
                  { key: ProductSpecifications.DiscsWidth, value: size.width + '' },
                  { key: ProductSpecifications.MinDiscsWidth, value: '' },
                  { key: ProductSpecifications.MaxDiscsWidth, value: '' },
                  { key: FilterParams.Axis, value: size.axis + '' },
                  { key: FilterParams.ModificationAuto, value: modification?.id + '' },
                ];

                const handlerSetCurrentSizeType = (value: string) => {
                  setCurrentSize(value);
                };

                return (
                  <RecommendedSizes.Checkbox
                    key={`${size.width}x${size.diameter}` + `${size.PCDc}*${size.PCDd} ET${size.et}` + `Dia${size.dia} ${type}`}
                    groupRadioButton={block.type}
                    title={title}
                    type={type}
                    size={diskSize}
                    value={`${size.width}x${size.diameter}` + `${size.PCDc}*${size.PCDd} ET${size.et}` + `Dia${size.dia}`}
                    isHidAxis={isHidAxis}
                    currentValue={currentSize}
                    setCurrentSize={handlerSetCurrentSizeType}
                  />
                );
              })}
              <div className={styles.WrapperContainerSparky}>
                {block.sparky.map((groupSparky, idx) => {
                  if (groupSparky.length === 0) {
                    return null;
                  }

                  const isHidAxisSparky = !groupSparky.some(x => [AxisType.frontAxles, AxisType.rearAxles].includes(x.axis));

                  return (
                    <div className={styles.WrapperGroupSparky} key={idx}>
                      <div className={styles.Line} />
                      <div className={styles.SparkyItems}>
                        {groupSparky
                          .sort(sortSizeDisks)
                          .sort(sortAxis)
                          .map(size => {
                            const title = `${size.width}x${size.diameter} ET${size.et}`;
                            const type = getTypeAxis(size.axis);
                            const diskSize = [
                              { key: ProductSpecifications.DiscsDiameter, value: size.diameter + '' },
                              { key: ProductSpecifications.DiscsPcd, value: `${size.PCDc}*${size.PCDd}` },
                              { key: ProductSpecifications.DiscsDia, value: size.dia + '' },
                              { key: ProductSpecifications.MinDiscsDia, value: '' },
                              { key: ProductSpecifications.MaxDiscsDia, value: '' },
                              { key: ProductSpecifications.DiscsEt, value: size.et + '' },
                              { key: ProductSpecifications.MinDiscsEt, value: '' },
                              { key: ProductSpecifications.MaxDiscsEt, value: '' },
                              { key: ProductSpecifications.DiscsWidth, value: size.width + '' },
                              { key: ProductSpecifications.MinDiscsWidth, value: '' },
                              { key: ProductSpecifications.MaxDiscsWidth, value: '' },
                              { key: FilterParams.Axis, value: size.axis + '' },
                              { key: FilterParams.ModificationAuto, value: modification?.id + '' },
                            ];

                            const handlerSetCurrentSizeType = (value: string) => {
                              setCurrentSize(value);
                            };

                            return (
                              <RecommendedSizes.Checkbox
                                key={`${size.width}x${size.diameter}` + `${size.PCDc}*${size.PCDd} ET${size.et}` + `Dia${size.dia} ${type}`}
                                groupRadioButton={block.type}
                                title={title}
                                type={type}
                                size={diskSize}
                                value={`${size.width}x${size.diameter}` + `${size.PCDc}*${size.PCDd} ET${size.et}` + `Dia${size.dia}`}
                                isHidAxis={isHidAxisSparky}
                                currentValue={currentSize}
                                setCurrentSize={handlerSetCurrentSizeType}
                              />
                            );
                          })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

RecommendedSizes.Checkbox = function Checkbox({
  groupRadioButton,
  title,
  type,
  size,
  value,
  className,
  currentValue,
  isHidAxis,
  setCurrentSize,
}: {
  groupRadioButton?: string;
  title: string;
  type: string;
  size: any;
  value: string;
  className?: string;
  currentValue: string;
  isHidAxis?: boolean;
  setCurrentSize: (payload: string) => void;
}) {
  const dispatch = useAppDispatch();

  const handlerSetParams = params => {
    setCurrentSize(value);
    dispatch(filterActions.setListParams(params));
  };

  return (
    <RadioButton
      id={'recommended-sizes ' + `${value}`.trim()}
      className={className}
      classNameContent={styles.RadioButton}
      checked={value === currentValue}
      value={value}
      dataTestId={`${value} ${groupRadioButton}`.trim()}
      onChange={() => handlerSetParams(size)}
    >
      <div className={styles.TitleRadioButton}>{title}</div>
      {!isHidAxis && <span className={styles.TypeRadioButton}>{type}</span>}
    </RadioButton>
  );
};
